import { useCallback, useMemo, useState } from 'react'

// ---------------------------------------------------------------------------
// Shared resolved-cluster review screen (the "resolve screen").
//
// The reconciliation review UI + apply-payload construction, shared by every
// write path (cv_upload, freeform). It renders the classifier's resolved
// clusters as one card per role and produces the apply payload; the PARENT owns
// the container chrome (modal vs page) and posts the payload to its own endpoint
// via onApply. DesignGate contract: docs/ux_decisions.md "CV-upload review" +
// "Freeform resolve".
// ---------------------------------------------------------------------------

// The disposition taxonomy (internal enum). The UI never shows these words —
// see ACTION_LABELS. "contradiction" is a flag, not a user-selectable action.
export type DispositionAction =
  | 'framing'
  | 'detail'
  | 'sibling'
  | 'new'
  | 'duplicate'
  | 'contradiction'

export interface SiblingOf {
  proof_id: string
  employer: string | null
  role_title: string | null
  position_id?: string | null
}

export interface Disposition {
  disposition_id: string
  temp_ids: string[]
  corroboration_count: number
  action: DispositionAction
  recommended_action: DispositionAction
  confidence: number
  source_text: string // neutral text of the incoming item — labeled on display
  proposed_addition?: string | null
  follow_on?: string | null
  conflict_description?: string | null
  contradiction_type?: string | null
  // present on new / sibling dispositions
  employer?: string
  role_title?: string
  period?: string | null
  competency_tags?: string[]
  context_tags?: string[]
  sibling_of?: SiblingOf | null
}

export interface Survivor {
  kind: 'library' | 'new'
  proof_id?: string | null
  employer?: string
  role_title?: string
  period?: string | null
  neutral_description?: string | null // labeled neutral description (Hard Rule 3 §5)
  summary?: string | null
}

export interface ResolvedCluster {
  cluster_id: string
  survivor: Survivor
  multi_to_one: boolean
  dispositions: Disposition[]
}

export interface ProfileChangeApplied {
  field: string
  change_type: string
  old: unknown
  new: unknown
}

export interface ReviewData {
  resolved_clusters: ResolvedCluster[]
  auto_dropped_count: number
  extraction_status?: 'success' | 'empty' | 'failed'
  // Conflict-replaces already applied at clarification time (shown as a done summary).
  profile_changes_applied?: ProfileChangeApplied[]
  // Non-conflicting profile changes to confirm/edit before they're written at apply.
  proposed_profile_changes?: ProfileChangeApplied[]
}

// An education `new` value; scalar/language changes carry a plain string instead.
interface EducationValue {
  institution?: string
  degree?: string
  year?: string
}

export interface ApplyPayload {
  new_proofs: ApplyNewProof[]
  enrichments: { accomplishment_id: string; proposed_addition: string }[]
  framings: { accomplishment_id: string; text: string }[]
  profile_changes: ProfileChangeApplied[]
}

// Friendly, non-internal disposition labels. "sibling" is an internal term and
// renders as "New proof in this role" everywhere in the UI.
const ACTION_LABELS: Record<Exclude<DispositionAction, 'contradiction'>, string> = {
  framing: 'New perspective',
  detail: 'New detail',
  sibling: 'New proof in this role',
  new: 'New proof',
  duplicate: "Don't add",
}
const ACTIONS_WITH_PROOF: DispositionAction[] = ['framing', 'detail', 'sibling', 'new', 'duplicate']
// A sibling is a NEW proof in an existing role — there is no specific matched
// accomplishment to fold into, so "New perspective"/"New detail" (which require a
// target proof) are not offered; only new-proof placements or "Don't add".
const ACTIONS_SIBLING: DispositionAction[] = ['sibling', 'new', 'duplicate']
const ACTIONS_NEW_ONLY: DispositionAction[] = ['new', 'duplicate']

// ---- review render models (one card per role) ----
interface ProofGroupModel {
  survivor: Survivor
  cluster: ResolvedCluster
  dispositions: Disposition[]
}
interface DispositionRef {
  cluster: ResolvedCluster
  disposition: Disposition
}
interface RoleCardModel {
  key: string
  employer: string
  role_title: string
  period: string | null
  isNew: boolean
  proofGroups: ProofGroupModel[]
  siblingItems: DispositionRef[]
  newItems: DispositionRef[]
}
interface ApplyNewProof {
  temp_id: string
  employer: string
  role_title: string
  period: string | null
  competency_tags: string[]
  context_tags: string[]
}

interface NewProofFieldEdit {
  employer?: string
  role_title?: string
  period?: string | null
}

interface ResolveReviewProps {
  review: ReviewData
  applying: boolean
  onApply: (payload: ApplyPayload) => void
  onDiscard: () => void
  incomingLabel: string // e.g. "From this upload" | "From your text"
  error?: string | null
  title?: string
  subtitle?: string
  applyLabel?: string
  discardLabel?: string
  onClose?: () => void // optional × in header (modal chrome)
}

export default function ResolveReview({
  review,
  applying,
  onApply,
  onDiscard,
  incomingLabel,
  error,
  title = 'Review',
  subtitle = 'Nothing is added to your Library until you confirm.',
  applyLabel = 'Add to Library',
  discardLabel = 'Discard',
  onClose,
}: ResolveReviewProps) {
  const [chosenActions, setChosenActions] = useState<Record<string, DispositionAction>>({})
  const [contradictionPicks, setContradictionPicks] = useState<
    Record<string, 'existing' | 'uploaded'>
  >({})
  const [detailEdits, setDetailEdits] = useState<Record<string, string>>({})
  const [newProofEdits, setNewProofEdits] = useState<Record<string, NewProofFieldEdit>>({})
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [nbaOpen, setNbaOpen] = useState(false)
  // Proposed profile changes (cv_upload): per-index skip flag + edited `new` value.
  const proposedProfile = review.proposed_profile_changes ?? []
  const [profileSkip, setProfileSkip] = useState<Record<number, boolean>>({})
  const [profileEdits, setProfileEdits] = useState<Record<number, unknown>>({})
  const profileValue = (i: number): unknown => profileEdits[i] ?? proposedProfile[i]?.new
  const includedProfileCount = proposedProfile.filter((_, i) => !profileSkip[i]).length

  const effectiveAction = useCallback(
    (d: Disposition): DispositionAction => chosenActions[d.disposition_id] ?? d.recommended_action,
    [chosenActions],
  )

  // The target existing proof for a framing/detail — the matched proof directly,
  // or (for a sibling) the proof it is a sibling of.
  const proofIdFor = (cluster: ResolvedCluster, d: Disposition): string | null =>
    cluster.survivor.proof_id ?? d.sibling_of?.proof_id ?? null

  // Group resolved clusters into ONE CARD PER ROLE (employer · role).
  const roleCards = useMemo(() => {
    const norm = (s?: string | null) => (s ?? '').trim().toLowerCase()
    const map = new Map<string, RoleCardModel>()
    const getCard = (
      key: string,
      base: Omit<RoleCardModel, 'proofGroups' | 'siblingItems' | 'newItems'>,
    ) => {
      let c = map.get(key)
      if (!c) {
        c = { ...base, proofGroups: [], siblingItems: [], newItems: [] }
        map.set(key, c)
      }
      return c
    }
    for (const cluster of review.resolved_clusters) {
      if (cluster.survivor.kind === 'library') {
        const s = cluster.survivor
        const card = getCard(`${norm(s.employer)}||${norm(s.role_title)}`, {
          key: `${norm(s.employer)}||${norm(s.role_title)}`,
          employer: s.employer ?? '',
          role_title: s.role_title ?? '',
          period: s.period ?? null,
          isNew: false,
        })
        card.proofGroups.push({ survivor: s, cluster, dispositions: cluster.dispositions })
      } else {
        const d = cluster.dispositions[0]
        if (!d) continue
        if (d.sibling_of) {
          const key = `${norm(d.sibling_of.employer)}||${norm(d.sibling_of.role_title)}`
          const card = getCard(key, {
            key,
            employer: d.sibling_of.employer ?? d.employer ?? '',
            role_title: d.sibling_of.role_title ?? d.role_title ?? '',
            period: d.period ?? null,
            isNew: false,
          })
          card.siblingItems.push({ cluster, disposition: d })
        } else {
          const key = `new||${cluster.cluster_id}`
          const card = getCard(key, {
            key,
            employer: d.employer ?? '',
            role_title: d.role_title ?? '',
            period: d.period ?? null,
            isNew: true,
          })
          card.newItems.push({ cluster, disposition: d })
        }
      }
    }
    return Array.from(map.values())
  }, [review])

  const skippedItems = useMemo(() => {
    const out: DispositionRef[] = []
    for (const cluster of review.resolved_clusters) {
      for (const d of cluster.dispositions) {
        if (d.recommended_action !== 'contradiction' && effectiveAction(d) === 'duplicate') {
          out.push({ cluster, disposition: d })
        }
      }
    }
    return out
  }, [review, effectiveAction])

  const clusterChanges = useMemo(() => {
    let n = 0
    for (const cluster of review.resolved_clusters) {
      for (const d of cluster.dispositions) {
        if (d.recommended_action === 'contradiction') {
          if (contradictionPicks[d.disposition_id] === 'uploaded') n += 1
        } else if (effectiveAction(d) !== 'duplicate') {
          n += 1
        }
      }
    }
    return n
  }, [review, effectiveAction, contradictionPicks])
  const totalChanges = clusterChanges + includedProfileCount

  const chooseAction = (dispositionId: string, action: DispositionAction) => {
    setChosenActions((prev) => ({ ...prev, [dispositionId]: action }))
    setOpenMenu(null)
  }
  const updateNewProofField = (id: string, field: keyof NewProofFieldEdit, value: string) => {
    setNewProofEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }))
  }
  const updateDetailText = (id: string, value: string) => {
    setDetailEdits((prev) => ({ ...prev, [id]: value }))
  }

  const buildPayload = (): ApplyPayload => {
    const new_proofs: ApplyNewProof[] = []
    const enrichments: { accomplishment_id: string; proposed_addition: string }[] = []
    const framings: { accomplishment_id: string; text: string }[] = []

    for (const cluster of review.resolved_clusters) {
      for (const d of cluster.dispositions) {
        const proofId = proofIdFor(cluster, d)
        if (d.recommended_action === 'contradiction') {
          if (contradictionPicks[d.disposition_id] === 'uploaded' && proofId) {
            const text = (detailEdits[d.disposition_id] ?? d.source_text).trim()
            if (text) enrichments.push({ accomplishment_id: proofId, proposed_addition: text })
          }
          continue
        }
        const action = effectiveAction(d)
        if (action === 'duplicate') continue
        if (action === 'framing' && proofId) {
          const text = (detailEdits[d.disposition_id] ?? d.source_text).trim()
          if (text) framings.push({ accomplishment_id: proofId, text })
        } else if (action === 'detail' && proofId) {
          const text = (detailEdits[d.disposition_id] ?? d.proposed_addition ?? d.source_text).trim()
          if (text) enrichments.push({ accomplishment_id: proofId, proposed_addition: text })
        } else if (action === 'sibling' || action === 'new') {
          const edit = newProofEdits[d.disposition_id] ?? {}
          const base = d.sibling_of
            ? {
                employer: d.sibling_of.employer ?? d.employer ?? '',
                role_title: d.sibling_of.role_title ?? d.role_title ?? '',
              }
            : { employer: d.employer ?? '', role_title: d.role_title ?? '' }
          new_proofs.push({
            temp_id: d.temp_ids[0],
            employer: edit.employer ?? base.employer,
            role_title: edit.role_title ?? base.role_title,
            period: edit.period ?? d.period ?? null,
            competency_tags: d.competency_tags ?? [],
            context_tags: d.context_tags ?? [],
          })
        }
      }
    }
    const profile_changes: ProfileChangeApplied[] = proposedProfile
      .map((c, i) => ({ c, i }))
      .filter(({ i }) => !profileSkip[i])
      .map(({ c, i }) => ({
        field: c.field,
        change_type: c.change_type,
        old: c.old,
        new: profileValue(i),
      }))
    return { new_proofs, enrichments, framings, profile_changes }
  }

  return (
    <div
      className="grid min-h-0 flex-1 bg-ob-surface font-body text-ob-on-surface"
      style={{ gridTemplateRows: 'auto minmax(0, 1fr) auto' }}
    >
      <div className="shrink-0 border-b border-ob-secondary/20 px-6 py-5 flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl text-ob-ink">{title}</h2>
          <p className="text-sm text-ob-secondary mt-1">{subtitle}</p>
          {review.auto_dropped_count > 0 && (
            <p className="text-xs text-ob-secondary mt-2">
              {review.auto_dropped_count}{' '}
              {review.auto_dropped_count === 1 ? 'item was' : 'items were'} already in your Library
              and won&rsquo;t be added again.
            </p>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            disabled={applying}
            className="text-ob-secondary hover:text-ob-ink text-xl leading-none disabled:opacity-40"
          >
            &times;
          </button>
        )}
      </div>

      {/* Disabled fieldset freezes every control below (dropdowns, editors,
          contradiction picks, Restore) in one wrapper while an apply is in flight,
          so nothing can be re-clicked or edited mid-write. */}
      <fieldset
        disabled={applying}
        className="px-5 py-5 space-y-5 overflow-y-auto flex-1 min-h-0 min-w-0 border-0 disabled:opacity-70"
      >
        {error && <p className="text-xs text-ob-error">{error}</p>}

        {review.profile_changes_applied && review.profile_changes_applied.length > 0 && (
          <div className="border border-ob-secondary/20 bg-ob-surface-card px-4 py-3 text-xs text-ob-secondary rounded-none">
            Updated your profile: {summariseProfileChanges(review.profile_changes_applied)}
          </div>
        )}

        {proposedProfile.length > 0 && (
          <section className="bg-ob-surface-card border border-ob-secondary/40 rounded-none">
            <div className="px-5 py-4 border-b border-ob-secondary/20">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-ob-accent">
                Your profile
              </div>
              <h3 className="font-serif text-lg text-ob-ink mt-1">Suggested profile updates</h3>
              <div className="text-xs text-ob-secondary mt-1">
                Confirm or edit each before it&rsquo;s saved. Nothing is written if you discard.
              </div>
            </div>
            {proposedProfile.map((change, i) => (
              <ProfileChangeRow
                key={i}
                change={change}
                value={profileValue(i)}
                skipped={!!profileSkip[i]}
                onToggle={() => setProfileSkip((prev) => ({ ...prev, [i]: !prev[i] }))}
                onChange={(v) => setProfileEdits((prev) => ({ ...prev, [i]: v }))}
              />
            ))}
          </section>
        )}

        {roleCards.map((card) => {
          const visibleGroups = card.proofGroups
            .map((pg) => ({
              pg,
              visible: pg.dispositions.filter(
                (d) => d.recommended_action === 'contradiction' || effectiveAction(d) !== 'duplicate',
              ),
            }))
            .filter((g) => g.visible.length > 0)
          const visibleSiblings = card.siblingItems.filter(
            (ref) => effectiveAction(ref.disposition) !== 'duplicate',
          )
          const visibleNew = card.newItems.filter(
            (ref) => effectiveAction(ref.disposition) !== 'duplicate',
          )
          const itemCount =
            visibleGroups.reduce((n, g) => n + g.visible.length, 0) +
            visibleSiblings.length +
            visibleNew.length
          if (itemCount === 0) return null
          return (
            <section
              key={card.key}
              className="bg-ob-surface-card border border-ob-secondary/40 rounded-none"
            >
              <div className="px-5 py-4 border-b border-ob-secondary/20">
                <div
                  className={`text-[11px] font-semibold uppercase tracking-wider ${
                    card.isNew ? 'text-ob-accent' : 'text-ob-secondary'
                  }`}
                >
                  {card.isNew ? 'New to your Library' : 'In your Library'}
                </div>
                <h3 className="font-serif text-lg text-ob-ink mt-1">
                  {card.employer} &middot; {card.role_title}
                </h3>
                <div className="text-xs text-ob-secondary mt-1">
                  {card.period ? `${card.period} · ` : ''}
                  {card.isNew
                    ? `${itemCount} new ${itemCount === 1 ? 'proof' : 'proofs'}`
                    : `${itemCount} ${itemCount === 1 ? 'item' : 'items'} to review in this role`}
                </div>
              </div>

              {visibleGroups.map(({ pg, visible }) => (
                <div key={pg.cluster.cluster_id} className="border-t border-ob-secondary/20">
                  <div className="px-5 pt-4 pb-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-ob-secondary mb-1.5">
                      Existing Proof in your Library
                    </div>
                    <div className="bg-ob-surface border border-ob-secondary/20 border-l-2 border-l-ob-secondary/40 px-3 py-2.5 text-sm text-ob-on-surface rounded-none">
                      {pg.survivor.neutral_description}
                      <span className="block mt-1.5 text-xs text-ob-secondary">
                        Neutral description — how this Proof is recorded internally, not the text on
                        your CV.
                      </span>
                    </div>
                    {pg.dispositions.length > 1 && (
                      <div className="mt-2 text-xs font-semibold text-ob-accent">
                        &#9888; {pg.dispositions.length} items landed on this Proof
                      </div>
                    )}
                  </div>
                  {visible.map((d) => (
                    <ReviewRow
                      key={d.disposition_id}
                      d={d}
                      incomingLabel={incomingLabel}
                      action={effectiveAction(d)}
                      options={ACTIONS_WITH_PROOF}
                      menuOpen={openMenu === d.disposition_id}
                      onToggleMenu={() =>
                        setOpenMenu((prev) => (prev === d.disposition_id ? null : d.disposition_id))
                      }
                      onChoose={(a) => chooseAction(d.disposition_id, a)}
                      contradictionPick={contradictionPicks[d.disposition_id]}
                      onPick={(p) =>
                        setContradictionPicks((prev) => ({ ...prev, [d.disposition_id]: p }))
                      }
                      detailText={detailEdits[d.disposition_id] ?? d.proposed_addition ?? d.source_text}
                      onDetailChange={(v) => updateDetailText(d.disposition_id, v)}
                      fieldEdit={newProofEdits[d.disposition_id] ?? {}}
                      onFieldChange={(f, v) => updateNewProofField(d.disposition_id, f, v)}
                    />
                  ))}
                </div>
              ))}

              {visibleSiblings.map(({ disposition: d }) => (
                <div key={d.disposition_id} className="border-t border-ob-secondary/20">
                  <div className="px-5 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ob-secondary">
                    New proof in this role
                  </div>
                  <ReviewRow
                    d={d}
                    incomingLabel={incomingLabel}
                    action={effectiveAction(d)}
                    options={ACTIONS_SIBLING}
                    menuOpen={openMenu === d.disposition_id}
                    onToggleMenu={() =>
                      setOpenMenu((prev) => (prev === d.disposition_id ? null : d.disposition_id))
                    }
                    onChoose={(a) => chooseAction(d.disposition_id, a)}
                    contradictionPick={contradictionPicks[d.disposition_id]}
                    onPick={(p) =>
                      setContradictionPicks((prev) => ({ ...prev, [d.disposition_id]: p }))
                    }
                    detailText={detailEdits[d.disposition_id] ?? d.proposed_addition ?? d.source_text}
                    onDetailChange={(v) => updateDetailText(d.disposition_id, v)}
                    fieldEdit={newProofEdits[d.disposition_id] ?? {}}
                    onFieldChange={(f, v) => updateNewProofField(d.disposition_id, f, v)}
                    siblingNote
                  />
                </div>
              ))}

              {visibleNew.map(({ disposition: d }) => (
                <div key={d.disposition_id} className="border-t border-ob-secondary/20">
                  <ReviewRow
                    d={d}
                    incomingLabel={incomingLabel}
                    action={effectiveAction(d)}
                    options={ACTIONS_NEW_ONLY}
                    menuOpen={openMenu === d.disposition_id}
                    onToggleMenu={() =>
                      setOpenMenu((prev) => (prev === d.disposition_id ? null : d.disposition_id))
                    }
                    onChoose={(a) => chooseAction(d.disposition_id, a)}
                    contradictionPick={contradictionPicks[d.disposition_id]}
                    onPick={(p) => setContradictionPicks((prev) => ({ ...prev, [d.disposition_id]: p }))}
                    detailText={detailEdits[d.disposition_id] ?? d.source_text}
                    onDetailChange={(v) => updateDetailText(d.disposition_id, v)}
                    fieldEdit={newProofEdits[d.disposition_id] ?? {}}
                    onFieldChange={(f, v) => updateNewProofField(d.disposition_id, f, v)}
                    editableFields
                  />
                </div>
              ))}
            </section>
          )
        })}

        {skippedItems.length > 0 && (
          <div className="pt-1">
            <button onClick={() => setNbaOpen((v) => !v)} className="text-sm text-ob-secondary">
              Not being added ({skippedItems.length}){' '}
              <span className="text-ob-accent">{nbaOpen ? '▾' : '▸'}</span>
            </button>
            {nbaOpen && (
              <div className="mt-2 border border-ob-secondary/20 bg-ob-surface-card rounded-none">
                {skippedItems.map(({ cluster, disposition: d }) => (
                  <div
                    key={d.disposition_id}
                    className="flex items-center justify-between gap-4 px-4 py-2.5 border-b border-ob-secondary/20 last:border-b-0"
                  >
                    <span className="text-sm text-ob-secondary truncate">{d.source_text}</span>
                    <button
                      onClick={() => {
                        if (d.recommended_action !== 'duplicate') {
                          chooseAction(d.disposition_id, d.recommended_action)
                        } else {
                          chooseAction(d.disposition_id, proofIdFor(cluster, d) ? 'detail' : 'new')
                        }
                      }}
                      className="shrink-0 border border-ob-secondary/40 text-ob-ink text-xs font-semibold px-3 py-1.5 rounded-none hover:bg-ob-surface"
                    >
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {totalChanges === 0 && skippedItems.length === 0 && proposedProfile.length === 0 && (
          <p className="text-sm text-ob-secondary text-center py-6">
            {review.auto_dropped_count > 0
              ? 'Everything here is already in your Library.'
              : review.extraction_status === 'empty'
                ? "We didn't find any new accomplishments."
                : 'No new proofs found.'}
          </p>
        )}
      </fieldset>

      <div className="shrink-0 bg-ob-surface border-t border-ob-secondary/20 px-6 py-4 flex justify-between items-center gap-4">
        <button
          onClick={onDiscard}
          disabled={applying}
          className="text-sm text-ob-secondary hover:text-ob-ink disabled:opacity-40"
        >
          {discardLabel}
        </button>
        <button
          onClick={() => onApply(buildPayload())}
          disabled={applying || totalChanges === 0}
          className="bg-ob-ink text-ob-on-ink px-6 py-3 text-sm font-semibold rounded-none disabled:opacity-40 hover:bg-ob-ink/90"
        >
          {applying ? 'Adding…' : applyLabel}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ReviewRow
// ---------------------------------------------------------------------------

interface ReviewRowProps {
  d: Disposition
  incomingLabel: string
  action: DispositionAction
  options: DispositionAction[]
  menuOpen: boolean
  onToggleMenu: () => void
  onChoose: (a: DispositionAction) => void
  contradictionPick?: 'existing' | 'uploaded'
  onPick: (p: 'existing' | 'uploaded') => void
  detailText: string
  onDetailChange: (v: string) => void
  fieldEdit: NewProofFieldEdit
  onFieldChange: (f: keyof NewProofFieldEdit, v: string) => void
  siblingNote?: boolean
  editableFields?: boolean
}

function ReviewRow({
  d,
  incomingLabel,
  action,
  options,
  menuOpen,
  onToggleMenu,
  onChoose,
  contradictionPick,
  onPick,
  detailText,
  onDetailChange,
  fieldEdit,
  onFieldChange,
  siblingNote,
  editableFields,
}: ReviewRowProps) {
  const isContradiction = d.recommended_action === 'contradiction'

  return (
    <div className="px-5 py-3.5 border-t border-ob-secondary/20 first:border-t-0 flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-start">
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-ob-secondary mb-1">
          {incomingLabel}
        </div>
        <p className="text-sm text-ob-on-surface">{d.source_text}</p>
        {d.corroboration_count > 1 && (
          <p className="mt-1 text-xs text-ob-secondary">seen in {d.corroboration_count} entries</p>
        )}
        {siblingNote &&
          (d.sibling_of?.position_id ? (
            <p className="mt-2 text-xs text-ob-secondary border-l-2 border-ob-secondary/40 pl-2">
              Will join your existing role: {d.sibling_of.employer} &middot; {d.sibling_of.role_title}
            </p>
          ) : (
            <p className="mt-2 text-xs text-ob-secondary border-l-2 border-ob-secondary/40 pl-2">
              Looks like the same role — you can confirm the position in your Library later.
            </p>
          ))}

        {isContradiction && (
          <div className="mt-3 border border-ob-secondary/40 border-l-2 border-l-ob-accent bg-ob-surface px-3.5 py-3 rounded-none">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ob-accent">
              &#9873; Conflict
            </div>
            <p className="mt-2 text-sm font-semibold text-ob-ink">Which value is right?</p>
            {d.conflict_description && (
              <p className="mt-1 text-xs text-ob-secondary">{d.conflict_description}</p>
            )}
            <div className="mt-2.5 flex flex-wrap gap-2.5">
              <button
                onClick={() => onPick('existing')}
                aria-pressed={contradictionPick === 'existing'}
                className={`text-sm px-3.5 py-2 border rounded-none ${
                  contradictionPick === 'existing'
                    ? 'bg-ob-ink text-ob-on-ink border-ob-ink'
                    : 'bg-ob-surface-card text-ob-ink border-ob-secondary/40'
                }`}
              >
                Keep what&rsquo;s in my Library
              </button>
              <button
                onClick={() => onPick('uploaded')}
                aria-pressed={contradictionPick === 'uploaded'}
                className={`text-sm px-3.5 py-2 border rounded-none ${
                  contradictionPick === 'uploaded'
                    ? 'bg-ob-ink text-ob-on-ink border-ob-ink'
                    : 'bg-ob-surface-card text-ob-ink border-ob-secondary/40'
                }`}
              >
                Use the new value
              </button>
            </div>
            <p className="mt-2.5 text-xs text-ob-secondary">Nothing is written until you confirm.</p>
          </div>
        )}

        {action === 'detail' && !isContradiction && (
          <div className="mt-3">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-ob-secondary mb-1">
              What to add to this Proof
            </label>
            <textarea
              value={detailText}
              onChange={(e) => onDetailChange(e.target.value)}
              rows={2}
              className="w-full border border-ob-secondary/40 bg-ob-surface px-3 py-1.5 text-sm text-ob-on-surface rounded-none focus:outline-none focus:border-ob-ink resize-y"
            />
          </div>
        )}

        {editableFields && (action === 'new' || action === 'sibling') && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-lg">
            <Field label="Employer">
              <input
                value={fieldEdit.employer ?? d.employer ?? ''}
                onChange={(e) => onFieldChange('employer', e.target.value)}
                className="w-full border border-ob-secondary/40 bg-ob-surface px-3 py-1.5 text-sm rounded-none focus:outline-none focus:border-ob-ink"
              />
            </Field>
            <Field label="Role">
              <input
                value={fieldEdit.role_title ?? d.role_title ?? ''}
                onChange={(e) => onFieldChange('role_title', e.target.value)}
                className="w-full border border-ob-secondary/40 bg-ob-surface px-3 py-1.5 text-sm rounded-none focus:outline-none focus:border-ob-ink"
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Period">
                <input
                  value={fieldEdit.period ?? d.period ?? ''}
                  onChange={(e) => onFieldChange('period', e.target.value)}
                  placeholder="e.g. 2022–2024"
                  className="w-full border border-ob-secondary/40 bg-ob-surface px-3 py-1.5 text-sm rounded-none focus:outline-none focus:border-ob-ink"
                />
              </Field>
            </div>
            {d.competency_tags && d.competency_tags.length > 0 && (
              <div className="sm:col-span-2 flex flex-wrap gap-2">
                {d.competency_tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-ob-secondary border border-ob-secondary/20 px-2 py-1 rounded-none"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!isContradiction && (
        <div className="relative shrink-0">
          <button
            onClick={onToggleMenu}
            className="inline-flex items-center justify-between gap-2 min-w-[210px] border border-ob-secondary/40 bg-ob-surface-card text-ob-ink text-xs font-semibold px-3 py-2 rounded-none"
          >
            <span>
              {ACTION_LABELS[action as Exclude<DispositionAction, 'contradiction'>]}
              {action === d.recommended_action && (
                <span className="text-[10px] font-bold uppercase tracking-wide text-ob-accent">
                  {' '}
                  &middot; Recommended
                </span>
              )}
            </span>
            <span className="text-ob-secondary text-[11px]">▾</span>
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-10 mt-1 min-w-[220px] border border-ob-secondary/40 bg-ob-surface-card rounded-none">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ob-secondary px-3 py-2 border-b border-ob-secondary/20">
                Add to your Library as…
              </div>
              {options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => onChoose(opt)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-sm text-left border-b border-ob-secondary/20 last:border-b-0 hover:bg-ob-surface"
                >
                  <span className={action === opt ? 'font-semibold text-ob-ink' : 'text-ob-on-surface'}>
                    {ACTION_LABELS[opt as Exclude<DispositionAction, 'contradiction'>]}
                  </span>
                  <span className={`text-ob-accent font-bold ${action === opt ? '' : 'invisible'}`}>
                    ✓
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-ob-secondary mb-1">{label}</label>
      {children}
    </div>
  )
}

const PROFILE_FIELD_LABEL: Record<string, string> = {
  full_name: 'name',
  email: 'email',
  phone: 'phone',
  linkedin_url: 'LinkedIn',
  location: 'location',
}

function profileChangeLabel(change: ProfileChangeApplied): string {
  switch (change.change_type) {
    case 'education_add':
      return 'Add this education entry'
    case 'education_enrich':
      return 'Update this education entry'
    case 'language_add':
      return 'Add this language'
    case 'language_enrich':
      return 'Update this language'
    case 'scalar_enrichment':
      return `Add your ${PROFILE_FIELD_LABEL[change.field] ?? change.field}`
    default:
      return 'Profile update'
  }
}

const PROFILE_INPUT_CLASS =
  'w-full border border-ob-secondary/40 bg-ob-surface px-3 py-1.5 text-sm rounded-none focus:outline-none focus:border-ob-ink disabled:opacity-50'

function ProfileChangeRow({
  change,
  value,
  skipped,
  onToggle,
  onChange,
}: {
  change: ProfileChangeApplied
  value: unknown
  skipped: boolean
  onToggle: () => void
  onChange: (v: unknown) => void
}) {
  const isEducation =
    change.change_type === 'education_add' || change.change_type === 'education_enrich'
  const edu = (value ?? {}) as EducationValue

  return (
    <div className="px-5 py-3.5 border-t border-ob-secondary/20 first:border-t-0 flex flex-col sm:flex-row gap-4 sm:items-start sm:justify-between">
      <div className={`flex-1 min-w-0 ${skipped ? 'opacity-50' : ''}`}>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-ob-secondary mb-2">
          {profileChangeLabel(change)}
        </div>
        {isEducation ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 max-w-xl">
            <input
              disabled={skipped}
              value={edu.institution ?? ''}
              onChange={(e) => onChange({ ...edu, institution: e.target.value })}
              placeholder="Institution"
              className={PROFILE_INPUT_CLASS}
            />
            <input
              disabled={skipped}
              value={edu.degree ?? ''}
              onChange={(e) => onChange({ ...edu, degree: e.target.value })}
              placeholder="Degree"
              className={PROFILE_INPUT_CLASS}
            />
            <input
              disabled={skipped}
              value={edu.year ?? ''}
              onChange={(e) => onChange({ ...edu, year: e.target.value })}
              placeholder="Year"
              className={PROFILE_INPUT_CLASS}
            />
          </div>
        ) : (
          <input
            disabled={skipped}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${PROFILE_INPUT_CLASS} max-w-md`}
          />
        )}
      </div>
      <label className="flex items-center gap-2 text-xs font-semibold text-ob-secondary cursor-pointer shrink-0 whitespace-nowrap">
        <input
          type="checkbox"
          checked={!skipped}
          onChange={onToggle}
          className="accent-ob-ink w-4 h-4"
        />
        {skipped ? "Won't add" : 'Add'}
      </label>
    </div>
  )
}

function summariseProfileChanges(changes: ProfileChangeApplied[]): string {
  const fieldLabel: Record<string, string> = {
    full_name: 'name',
    email: 'email',
    phone: 'phone',
    linkedin_url: 'LinkedIn',
    location: 'location',
    education: 'education',
    languages: 'languages',
  }
  const parts: string[] = []
  const enrichedScalars = changes.filter((c) => c.change_type === 'scalar_enrichment')
  const eduAdds = changes.filter((c) => c.change_type === 'education_add').length
  const eduEnriches = changes.filter((c) => c.change_type === 'education_enrich').length
  const langAdds = changes.filter((c) => c.change_type === 'language_add').length
  const langEnriches = changes.filter((c) => c.change_type === 'language_enrich').length

  for (const c of enrichedScalars) {
    parts.push(`filled in your ${fieldLabel[c.field] ?? c.field}`)
  }
  if (eduAdds > 0) parts.push(`added ${eduAdds} education entr${eduAdds === 1 ? 'y' : 'ies'}`)
  if (eduEnriches > 0)
    parts.push(`enriched ${eduEnriches} education entr${eduEnriches === 1 ? 'y' : 'ies'}`)
  if (langAdds > 0) parts.push(`added ${langAdds} language${langAdds === 1 ? '' : 's'}`)
  if (langEnriches > 0)
    parts.push(`updated ${langEnriches} language proficienc${langEnriches === 1 ? 'y' : 'ies'}`)

  if (parts.length > 3) {
    const overflow = parts.length - 3
    return `${parts.slice(0, 3).join(', ')}, and ${overflow} more`
  }
  return `${parts.join(', ')}.`
}
